import React, { useEffect, useState, useMemo, useCallback } from "react"
import { FaTrashAlt } from "react-icons/fa"
import { RiExpandDiagonalFill } from "react-icons/ri"
import Modal from "./Modal"
import _ from "lodash"
import debounce from "lodash/debounce"
import { PageLoader } from "../Authentication/page-loader"

const AcuityTable = ({
	hasWriteSettingsPermission,
	acuityTable,
	setAcuityTable,
	nurses,
	setAcuityTableModified
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [showAllNurses, setShowAllNurses] = useState({})
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")

	const [initialAcuityData, setInitialAcuityData] = useState(acuityTable)

	useEffect(() => {
		if (acuityTable && acuityTable.length > 0 && initialAcuityData.length === 0) {
			setInitialAcuityData(_.cloneDeep(acuityTable))
		}
	}, [acuityTable, initialAcuityData])
	//set original acuity table once here not every time acuity table changes so dont use a use effect

	const hasAcuityTableChanged = useCallback(() => {
		if (initialAcuityData.length === 0) {
			setInitialAcuityData(acuityTable)
		}
		return !_.isEqual(acuityTable, initialAcuityData)
	}, [acuityTable, initialAcuityData])

	// Debounce search query
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSetSearchQuery = useCallback(
		debounce(query => setDebouncedSearchQuery(query), 300),
		[]
	)

	useEffect(() => {
		debouncedSetSearchQuery(searchQuery)
	}, [searchQuery, debouncedSetSearchQuery])

	// Create a smaller array of names for filtering
	const infusionNames = useMemo(() => {
		return acuityTable.map(acuity => acuity.name?.toLowerCase() || "")
	}, [acuityTable])

	// Filter acuity fields based on search query
	const filteredAcuityFields = useMemo(() => {
		if (!debouncedSearchQuery.trim()) return acuityTable
		const searchTerms = debouncedSearchQuery
			.toLowerCase()
			.split(" ")
			.filter(term => term)
		return infusionNames
			.filter(name => {
				return searchTerms.every(term => name.includes(term))
			})
			.map(filteredName =>
				acuityTable.find(acuity => acuity.name?.toLowerCase() === filteredName)
			)
	}, [acuityTable, debouncedSearchQuery, infusionNames])

	// Toggle show all nurses
	const toggleShowAllNurses = useCallback(index => {
		setShowAllNurses(prev => ({
			...prev,
			[index]: !prev[index]
		}))
	}, [])

	// Get visible nurses
	const getVisibleNurses = useCallback(
		(treatment, index) => {
			return (
				treatment &&
				treatment.eligibleNurses
					.filter(nurse => nurse.eligible)
					.slice(0, showAllNurses[index] ? treatment.eligibleNurses.length : 5)
			)
		},
		[showAllNurses]
	)

	// // Initialize original values
	// useEffect(() => {
	// 	setOriginalAcuityTable(JSON.parse(JSON.stringify(acuityTable)))
	// }, [acuityTable])

	// Helper function to check if tables are different

	// Update the modified flag whenever acuity table changes
	// useEffect(() => {
	// 	setAcuityTableModified(hasTableChanged())
	// }, [acuityTable, hasTableChanged, setAcuityTableModified])

	// Update acuity value
	const updateAcuityValue = (index, newValue) => {
		setAcuityTable(prev => {
			const updated = [...prev]
			updated[index].value = newValue
			setAcuityTableModified(hasAcuityTableChanged())
			return updated
		})
	}

	// Update resource preferences
	const updateResourcePreference = (index, resource, isSelected) => {
		setAcuityTable(prev => {
			const updated = [...prev]
			const resourceRule = updated[index].rules.find(rule => rule.type === "resource")
			if (resourceRule) {
				resourceRule.value[resource] = isSelected
			}
			setAcuityTableModified(hasAcuityTableChanged())
			return updated
		})
	}

	// Update isolation status
	const updateIsolationStatus = (index, resource, isSelected) => {
		setAcuityTable(prev => {
			const updated = [...prev]
			const isolationRule = updated[index].rules.find(rule => rule.type === "isolation")
			if (isolationRule) {
				isolationRule.value[resource] = isSelected
			}
			setAcuityTableModified(hasAcuityTableChanged())
			return updated
		})
	}

	// Add or remove a nurse
	const toggleNurseEligibility = (index, nurseId) => {
		setAcuityTable(prev => {
			const updated = [...prev]
			const nurse = updated[index].eligibleNurses.find(n => n.nurseId === nurseId)
			if (nurse) {
				nurse.eligible = !nurse.eligible
			}
			setAcuityTableModified(hasAcuityTableChanged())
			return updated
		})
	}

	if (!acuityTable || acuityTable.length === 0 || !nurses) {
		return <PageLoader />
	}

	return (
		<div className="mt-10">
			<div className="flex justify-between items-center mb-4">
				<h2 className="text-2xl font-semibold text-gray-900">Acuity Mixes</h2>
				<button
					type="button"
					onClick={() => setIsModalOpen(true)}
					className="flex items-center justify-center p-2 bg-customBlue text-white rounded-md hover:bg-customBlue-dark focus:outline-none shadow-md transition duration-200"
					aria-label="Expand"
				>
					<RiExpandDiagonalFill size={20} />
				</button>
			</div>

			<p className="text-sm text-gray-500 mt-2">
				{`Total Treatments: ${filteredAcuityFields.length}, `}
				{Object.entries(
					filteredAcuityFields.reduce((acc, acuity) => {
						const acuityValue = acuity.value
						if (acuityTable.some(item => item.name === acuity.name)) {
							acc[acuityValue] = (acc[acuityValue] || 0) + 1
						}
						return acc
					}, {})
				).map(([acuity, count]) => (
					<span key={acuity}>{`Acuity ${acuity}: ${count}, `}</span>
				))}
			</p>

			<div className="overflow-x-auto">
				<div className="max-h-96 overflow-y-auto">
					<div className="mb-4">
						<input
							type="text"
							placeholder="Search treatments..."
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
						/>
					</div>
					<table
						className="min-w-full divide-y divide-gray-200 mt-4 table-auto"
						style={{ width: "120%" }}
					>
						<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Medication Procedure
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Acuity
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Resource Preferences
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Isolation Status
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase">
									Eligible Nurses
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{acuityTable.map((acuity, index) => {
								const filteredTreatment = filteredAcuityFields.find(
									item => item.appointmentType === acuity.appointmentType
								)
								return (
									filteredTreatment && (
										<tr
											key={`${acuity.appointmentType?.toLowerCase()}-${index}`}
										>
											<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
												<p className="block w-full rounded-md py-1.5 text-gray-900 sm:text-sm">
													{acuity.name}
												</p>
											</td>
											<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
												{hasWriteSettingsPermission ? (
													<input
														type="number"
														defaultValue={
															filteredTreatment
																? filteredTreatment.value
																: 0
														}
														onChange={e =>
															updateAcuityValue(index, e.target.value)
														}
														className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
													/>
												) : (
													<p className="block w-full rounded-md py-1.5 text-gray-900">
														{filteredTreatment
															? filteredTreatment.value
															: 0}
													</p>
												)}
											</td>
											<td className="px-10 py-4 whitespace-nowrap text-sm text-gray-900">
												<div className="space-y-2">
													{Object.entries(
														filteredTreatment?.rules?.find(
															rule => rule.type === "resource"
														)?.value || {}
													).map(([resource, isSelected], idx) => (
														<div
															key={idx}
															className="flex items-center"
														>
															<input
																type="checkbox"
																checked={isSelected}
																onChange={e =>
																	hasWriteSettingsPermission &&
																	updateResourcePreference(
																		index,
																		resource,
																		e.target.checked
																	)
																}
																disabled={
																	!hasWriteSettingsPermission
																}
																className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
															/>
															<label className="ml-2 text-sm text-gray-900">
																{resource.charAt(0).toUpperCase() +
																	resource.slice(1)}
															</label>
														</div>
													))}
												</div>
											</td>
											<td className="px-10 py-4 whitespace-nowrap text-sm text-gray-900">
												<div className="space-y-2">
													{Object.entries(
														filteredTreatment?.rules?.find(
															rule => rule.type === "isolation"
														)?.value || {}
													).map(([resource, isSelected], idx) => (
														<div
															key={idx}
															className="flex items-center"
														>
															<input
																type="checkbox"
																checked={isSelected}
																onChange={e =>
																	hasWriteSettingsPermission &&
																	updateIsolationStatus(
																		index,
																		resource,
																		e.target.checked
																	)
																}
																disabled={
																	!hasWriteSettingsPermission
																}
																className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
															/>
															<label className="ml-2 text-sm text-gray-900">
																{resource.charAt(0).toUpperCase() +
																	resource.slice(1)}
															</label>
														</div>
													))}
												</div>
											</td>
											<td className="px-10 py-4 text-sm text-gray-900">
												{getVisibleNurses(filteredTreatment, index).map(
													(nurse, nurseIndex) => {
														const matchedNurse = nurses.find(
															n => n.nurseId === nurse.nurseId
														)
														return (
															<div
																key={`${nurse.nurseId}-${nurseIndex}`}
																className="flex items-center justify-between mt-2"
															>
																<span>
																	{matchedNurse
																		? matchedNurse.nurseName
																		: "Unknown Nurse"}
																</span>
																{hasWriteSettingsPermission && (
																	<button
																		onClick={() =>
																			toggleNurseEligibility(
																				index,
																				nurse.nurseId
																			)
																		}
																		className="text-red-500 hover:text-red-700"
																	>
																		<FaTrashAlt />
																	</button>
																)}
															</div>
														)
													}
												)}
												{filteredTreatment.eligibleNurses.filter(
													nurse => nurse.eligible
												).length > 5 && (
													<button
														onClick={() => toggleShowAllNurses(index)}
														type="button"
														className="text-blue-500 hover:text-blue-700 mt-2"
													>
														{showAllNurses[index]
															? "Show Less"
															: "Show More"}
													</button>
												)}

												{nurses.some(
													nurse =>
														!filteredTreatment.eligibleNurses.some(
															eligibleNurse =>
																eligibleNurse.nurseId ===
																nurse.nurseId
														)
												) && (
													<div className="mt-4">
														<select
															onChange={e => {
																const selectedNurse = nurses.find(
																	nurse =>
																		nurse.nurseName ===
																		e.target.value
																)
																if (selectedNurse) {
																	toggleNurseEligibility(
																		index,
																		selectedNurse.nurseId
																	)
																}
															}}
															className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
														>
															<option value="">
																Select Nurse to Add
															</option>
															{nurses
																.filter(
																	nurse =>
																		!filteredTreatment.eligibleNurses.some(
																			eligibleNurse =>
																				eligibleNurse.nurseId ===
																				nurse.nurseId
																		)
																) // Show nurses not eligible yet and not already listed
																.map(nurse => (
																	<option
																		key={nurse.nurseId}
																		value={nurse.nurseName}
																	>
																		{nurse.nurseName}
																	</option>
																))}
														</select>
													</div>
												)}
											</td>
										</tr>
									)
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

			<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
				<div className="overflow-x-auto max-h-screen">
					<h2 className="text-2xl font-semibold text-gray-900">Acuity Mixes</h2>
					<p className="text-sm text-gray-500 mt-2">
						{`Total Treatments: ${acuityTable.length}, `}
						{Object.entries(
							acuityTable.reduce((acc, acuity) => {
								const acuityValue = acuity.value
								acc[acuityValue] = (acc[acuityValue] || 0) + 1
								return acc
							}, {})
						).map(([acuity, count]) => (
							<span key={acuity}>{`Acuity ${acuity}: ${count}, `}</span>
						))}
					</p>
					<div className="mb-4">
						{/* <input
              type="text"
              placeholder="Search treatments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            /> */}
					</div>
					<table
						className="min-w-full divide-y divide-gray-200 mt-4 table-auto"
						style={{ width: "120%" }}
					>
						<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Medication Procedure
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Acuity
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Resource Preferences
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Isolation Status
								</th>
								<th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase">
									Eligible Nurses
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{filteredAcuityFields.map((acuity, index) => (
								<tr key={`${acuity.name?.toLowerCase()}-${index}`}>
									<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
										<p className="block w-full rounded-md py-1.5 text-gray-900 sm:text-sm">
											{acuity.name}
										</p>
									</td>
									<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
										<input
											type="number"
											defaultValue={acuity.value}
											onChange={e => updateAcuityValue(index, e.target.value)}
											className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
										/>
									</td>
									<td className="px-10 py-4 whitespace-nowrap text-sm text-gray-900">
										<ul className="list-disc list-inside">
											{Object.entries(
												acuity.rules?.find(rule => rule.type === "resource")
													?.value || {}
											).map(([resource, isSelected], idx) => (
												<div key={idx} className="flex items-center">
													<input
														type="checkbox"
														checked={isSelected}
														onChange={e =>
															updateResourcePreference(
																index,
																resource,
																e.target.checked
															)
														}
														className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
													/>
													<label className="ml-2 text-sm text-gray-900">
														{resource.charAt(0).toUpperCase() +
															resource.slice(1)}
													</label>
												</div>
											))}
										</ul>
									</td>
									<td className="px-10 py-4 whitespace-nowrap text-sm text-gray-900">
										<ul className="list-disc list-inside">
											{Object.entries(
												acuity.rules?.find(
													rule => rule.type === "isolation"
												)?.value || {}
											).map(([resource, isSelected], idx) => (
												<div key={idx} className="flex items-center">
													<input
														type="checkbox"
														checked={isSelected}
														onChange={e =>
															updateIsolationStatus(
																index,
																resource,
																e.target.checked
															)
														}
														className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
													/>
													<label className="ml-2 text-sm text-gray-900">
														{resource.charAt(0).toUpperCase() +
															resource.slice(1)}
													</label>
												</div>
											))}
										</ul>
									</td>
									<td className="px-10 py-4 text-sm text-gray-900">
										{getVisibleNurses(acuity, index).map(
											(nurse, nurseIndex) => {
												const matchedNurse = nurses.find(
													n => n.nurseId === nurse.nurseId
												)
												return (
													<div
														key={`${nurse.nurseId}-${nurseIndex}`}
														className="flex items-center justify-between mt-2"
													>
														<span>
															{matchedNurse
																? matchedNurse.nurseName
																: "Unknown Nurse"}
														</span>
														{hasWriteSettingsPermission && (
															<button
																onClick={() =>
																	toggleNurseEligibility(
																		index,
																		nurse.nurseId
																	)
																}
																className="text-red-500 hover:text-red-700"
															>
																<FaTrashAlt />
															</button>
														)}
													</div>
												)
											}
										)}
										{acuity.eligibleNurses.filter(nurse => nurse.eligible)
											.length > 5 && (
											<button
												onClick={() => toggleShowAllNurses(index)}
												type="button"
												className="text-blue-500 hover:text-blue-700 mt-2"
											>
												{showAllNurses[index] ? "Show Less" : "Show More"}
											</button>
										)}

										{nurses.some(
											nurse =>
												!acuity.eligibleNurses.some(
													eligibleNurse =>
														eligibleNurse.nurseId === nurse.nurseId
												)
										) && (
											<div className="mt-4">
												<select
													onChange={e => {
														const selectedNurse = nurses.find(
															nurse =>
																nurse.nurseName === e.target.value
														)
														if (selectedNurse) {
															toggleNurseEligibility(
																index,
																selectedNurse.nurseId
															)
														}
													}}
													className="block w-full rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												>
													<option value="">Select Nurse to Add</option>
													{nurses
														.filter(
															nurse =>
																!acuity.eligibleNurses.some(
																	eligibleNurse =>
																		eligibleNurse.nurseId ===
																		nurse.nurseId
																)
														) // Show nurses not eligible yet and not already listed
														.map(nurse => (
															<option
																key={nurse.nurseId}
																value={nurse.nurseName}
															>
																{nurse.nurseName}
															</option>
														))}
												</select>
											</div>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Modal>
		</div>
	)
}

export default React.memo(AcuityTable)
